import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';

import { Store } from '@dojo/framework/stores/Store';
// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';

import { BackView } from '@holmescorp/backview';
import {
	pathmap,
	userstate,
	createNlsMiddleware,
	expandedTopicChange,
	trackingWYLOChange,
	documentTitleUpdate,
	lccstate
} from '@holmescorp/lcc-state';

import { quizstate, initialState } from '../support/state';
import { createProgressionMiddleware } from '../middleware/progression';
import { Question, QuizMeta } from '../interfaces';

import css from '../css/quizfolder.m.css';

export interface QuizTitleProperties {
	title: string;
	progression: string;
	store: Store<QuizMeta>;
}

const nls = createNlsMiddleware('quiz');
const progressions = createProgressionMiddleware();
const factory = create({ nls, theme, userstate, quizstate, progressions, lccstate }).properties<QuizTitleProperties>();

export const QuizTitle = factory(
	({ middleware: { nls, theme, userstate, quizstate, progressions, lccstate }, properties }) => {
		const { store, progression, key } = properties();
		quizstate.setStore(store);

		// additional properties
		const keyprops = () => {
			const { get, path } = quizstate;
			const quiz = get(path('quiz'));
			const { title } = quiz || { title: '' };
			const quizkind = quiz ? quiz.kind : '';
			const info = get(path('info'));
			const isaudit = get(path('isaudit'));
			const numberofquestions = info ? (isaudit ? info.lastresult.numberofquestions : info.numberofquestions) : 0;
			// autostarted quizzes don't have a quiz-begin screen
			const isautostart = get(path('isautostart'));
			return {
				question: get(path('question')) as Question,
				inreview: get(path('inreview')) as boolean,
				quizkind,
				title,
				numberofquestions,
				isautostart
			};
		};

		const { question, inreview, quizkind, numberofquestions, title, isautostart = false } = keyprops();
		const {
			isPlaceholder,
			messages: { question: questionText, of: ofText, item: itemText }
		} = nls();
		if (isPlaceholder) {
			return null;
		}
		const assoc = window.hcstatic.association_name;
		const product = lccstate.get(lccstate.path('product'));

		const {
			titleBlock,
			title: titleCss,
			questionBlock,
			count: countCss,
			question: questionCss
		} = theme.classes(css);

		const noBackButton = quizkind === undefined; // || quizkind === 'pretest' || quizkind === 'posttest';
		const showItem = quizkind !== 'activity';
		const showQuestionBlock =
			question !== undefined && progression !== 'singlestep' && inreview === false && numberofquestions > 1;

		const overrideSelected = () => {
			const { get, path } = userstate;
			const location = get(path('location'));
			const [, explore, , topicset, topicgroup, topic] = location.split('/');
			trackingWYLOChange({ tracking: {} });
			expandedTopicChange({ topicid: topicset });
			const view = showQuestionBlock
				? () => {
						initialState('', store);
						progressions.setStore(store);
						progressions.initialize();
						return pathmap.get(location)!.view;
				  }
				: () => pathmap.get('/explore')!.view;

			view()();
		};

		const questionblock = (): DNode => {
			const { cursor = 0, id = '' } = question;
			const questionLabel = `${questionText} ${cursor + 1} ${ofText} ${numberofquestions}`;

			const newTitle = `${title} - ${questionLabel} - ${assoc?.toLocaleUpperCase()} - ${product.toLocaleUpperCase()}`;
			documentTitleUpdate({ title: newTitle });

			return v('div', { key: 'question-block', classes: [questionBlock] }, [
				v('div', { key: 'q-of', classes: [countCss] }, [questionLabel]),
				v('div', { key: 'q-id', classes: [questionCss] }, [showItem ? `${itemText} ${id}` : ''])
			]);
		};

		return v('div', { key, classes: [titleBlock] }, [
			v('h1', { key: `title-${key}`, classes: [titleCss] }, [`${title}`]),
			noBackButton ? null : w(BackView, isautostart ? {} : { overrideSelected }),
			showQuestionBlock ? questionblock() : v('div', { key: 'empty-qblock', classes: [questionBlock] })
		]);
	}
);
